
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import PageFooter from '../../components/feature/PageFooter';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function ExperiencePage() {
  const [loaded, setLoaded] = useState(false);
  const { ref: workRef, isVisible: workVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: eduRef, isVisible: eduVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    setLoaded(true);
  }, []);

  const workExperiences = [
    {
      period: '2024.11 — 2025.6',
      duration: '8 個月',
      title: '法務專員',
      company: '律師事務所',
      location: '台灣',
      type: '全職',
      tags: ['訴訟文書', '合約審閱', '法律諮詢', '法律研究'],
      points: [
        '草擬訴訟文書、審約，處理一般法律諮詢事項',
        '執行法律資料檢索，支援案件分析與決策準備',
      ],
      icon: 'ri-scales-3-line',
      color: 'from-orange-500 to-rose-500',
      current: false,
    },
    {
      period: '2022.12 — 2024.10',
      duration: '1 年 10 個月',
      title: '行政秘書',
      company: '律師事務所',
      location: '台灣',
      type: '全職',
      tags: ['案件流程管理', '文件標準化', '跨機關協調', '行政管理'],
      points: [
        '負責訴訟案件流程管理，確保案件作業時效與準確性',
        '法律文件標準化整理，優化文件格式與作業流程',
        '與法院、地政及戶政等機關溝通協作，整理案件所需資料並追蹤處理進度',
      ],
      icon: 'ri-briefcase-3-line',
      color: 'from-rose-400 to-rose-600',
      current: false,
    },
  ];

  const education = [
    {
      period: '—',
      degree: '學士學位',
      school: '私立東吳大學',
      department: '法律學系',
      gpa: '',
      icon: 'ri-graduation-cap-line',
      color: 'from-amber-400 to-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-sm font-semibold text-orange-700">工作經歷</span>
          </div>
          <h1
            className={`text-4xl md:text-6xl font-bold text-gray-900 mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
          >
            經歷與<br />學歷
          </h1>
          <p
            className={`text-lg text-gray-600 max-w-2xl mx-auto transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            法律背景出身，持續精進數據分析能力，邁向資料領域的職涯轉型
          </p>
        </div>
      </section>

      {/* Work Experience Timeline */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white" ref={workRef}>
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className={`flex items-center gap-4 mb-8 md:mb-14 transition-all duration-700 ${workVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-briefcase-4-line text-white text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>工作經歷</h2>
              <p className="text-sm text-gray-500">Work Experience</p>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-300 via-rose-300 to-transparent"></div>

            <div className="space-y-10">
              {workExperiences.map((exp, i) => (
                <div
                  key={i}
                  className={`relative pl-16 transition-all duration-700 ${workVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${i * 200}ms` }}
                >
                  {/* Timeline dot */}
                  <div className={`absolute left-0 top-1 w-12 h-12 bg-gradient-to-br ${exp.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <i className={`${exp.icon} text-white text-xl`}></i>
                  </div>

                  {/* Card */}
                  <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    {/* Header */}
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {exp.title}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-600">
                          <i className="ri-building-line text-orange-500"></i>
                          <span className="text-base font-semibold">{exp.company}</span>
                          <span className="text-gray-400">·</span>
                          <span className="text-sm">{exp.location}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-2 justify-end mb-1">
                          {exp.current && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                              在職中
                            </span>
                          )}
                          <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">{exp.type}</span>
                        </div>
                        <p className="text-sm text-gray-500">{exp.period}</p>
                        <p className="text-xs text-orange-600 font-semibold">{exp.duration}</p>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-5">
                      {exp.tags.map((tag, j) => (
                        <span key={j} className="px-3 py-1 bg-orange-50 text-orange-700 text-xs font-semibold rounded-full border border-orange-100">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Bullet points */}
                    <ul className="space-y-2.5">
                      {exp.points.map((point, j) => (
                        <li
                          key={j}
                          className={`flex items-start gap-3 text-sm text-gray-600 leading-relaxed transition-all duration-500 ${workVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                          style={{ transitionDelay: `${i * 200 + 200 + j * 60}ms` }}
                        >
                          <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}

              {/* Future / Seeking */}
              <div
                className={`relative pl-16 transition-all duration-700 ${workVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                style={{ transitionDelay: '400ms' }}
              >
                <div className="absolute left-0 top-1 w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl flex items-center justify-center shadow border-2 border-dashed border-orange-300">
                  <i className="ri-search-2-line text-orange-400 text-xl"></i>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-rose-50 border border-dashed border-orange-200 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-700 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                    數據分析師 / 數據工程師
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">積極轉職中 · 目標職位</p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    結合法律背景的嚴謹思維與正在培養的資料分析能力，期望加入重視數據驅動決策的團隊，持續精進 Python、SQL 與資料視覺化技術。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-gray-50 to-orange-50" ref={eduRef}>
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className={`flex items-center gap-4 mb-8 md:mb-14 transition-all duration-700 ${eduVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-graduation-cap-line text-white text-2xl"></i>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>學歷</h2>
              <p className="text-sm text-gray-500">Education</p>
            </div>
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-300 to-transparent"></div>

            <div className="space-y-10">
              {education.map((edu, i) => (
                <div
                  key={i}
                  className={`relative pl-16 transition-all duration-700 ${eduVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${i * 200}ms` }}
                >
                  <div className={`absolute left-0 top-1 w-12 h-12 bg-gradient-to-br ${edu.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <i className={`${edu.icon} text-white text-xl`}></i>
                  </div>

                  <div className="bg-white border border-gray-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                          {edu.degree}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <i className="ri-building-4-line text-orange-500"></i>
                          <span className="text-base font-semibold">{edu.school}</span>
                        </div>
                        <p className="text-sm text-gray-500">{edu.department}</p>
                        {edu.gpa && <p className="text-xs text-orange-600 font-semibold mt-1">GPA：{edu.gpa}</p>}
                      </div>
                      {edu.period !== '—' && (
                        <p className="text-sm text-gray-500 flex-shrink-0">{edu.period}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
